import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Star, DollarSign, BarChart } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ImageUpload from '@/components/admin/ImageUpload';
import { adminApi } from '@/lib/api';

const AdminCoachingServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    tagline: '',
    short_description: '',
    hero_description: '',
    image: '',
    icon: 'FileText',
    pricing_one_time: 250,
    pricing_installment: 65,
    pricing_installment_frequency: 'bi-weekly',
    stat_1_label: '',
    stat_1_value: '',
    stat_2_label: '',
    stat_2_value: '',
    is_popular: false,
    is_active: true,
    order: 0,
    learn_more_link: '',
    cta_link: ''
  });

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const res = await adminApi.getCoachingServices();
      setServices(res.data);
    } catch (error) {
      toast.error('Failed to fetch coaching services');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      tagline: '',
      short_description: '',
      hero_description: '',
      image: '',
      icon: 'FileText',
      pricing_one_time: 250,
      pricing_installment: 65,
      pricing_installment_frequency: 'bi-weekly',
      stat_1_label: '',
      stat_1_value: '',
      stat_2_label: '',
      stat_2_value: '',
      is_popular: false,
      is_active: true,
      order: services.length,
      learn_more_link: '',
      cta_link: ''
    });
    setEditing(null);
  };

  const handleEdit = (service) => {
    setEditing(service);
    setFormData({
      title: service.title || '',
      slug: service.slug || '',
      tagline: service.tagline || '',
      short_description: service.short_description || '',
      hero_description: service.hero_description || '',
      image: service.image || '',
      icon: service.icon || 'FileText',
      pricing_one_time: service.pricing_one_time || 250,
      pricing_installment: service.pricing_installment || 65,
      pricing_installment_frequency: service.pricing_installment_frequency || 'bi-weekly',
      stat_1_label: service.stat_1_label || '',
      stat_1_value: service.stat_1_value || '',
      stat_2_label: service.stat_2_label || '',
      stat_2_value: service.stat_2_value || '',
      is_popular: service.is_popular || false,
      is_active: service.is_active !== false,
      order: service.order || 0,
      learn_more_link: service.learn_more_link || '',
      cta_link: service.cta_link || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.short_description) {
      toast.error('Please fill in required fields');
      return;
    }

    // Auto-generate slug if empty
    const slug = formData.slug || formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const submitData = { ...formData, slug };

    try {
      if (editing) {
        await adminApi.updateCoachingService(editing.id, submitData);
        toast.success('Service updated');
      } else {
        await adminApi.createCoachingService(submitData);
        toast.success('Service created');
      }
      setDialogOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.deleteCoachingService(id);
      toast.success('Service deleted');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const iconOptions = [
    { value: 'FileText', label: 'Resume/Document' },
    { value: 'Linkedin', label: 'LinkedIn' },
    { value: 'UserCheck', label: 'Interview' },
    { value: 'Target', label: 'Strategy/Target' },
    { value: 'Briefcase', label: 'Career/Briefcase' },
    { value: 'Share2', label: 'Networking' },
    { value: 'Search', label: 'Discovery/Search' },
    { value: 'TrendingUp', label: 'Growth' },
    { value: 'Award', label: 'Achievement' },
    { value: 'MessageSquare', label: 'Consulting' }
  ];

  return (
    <div className="space-y-6" data-testid="admin-coaching-services">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Coaching Services</h1>
          <p className="text-muted-foreground">Manage individual coaching services displayed on the Services page</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-coaching-service-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Coaching Service' : 'New Coaching Service'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="pricing">Pricing & Stats</TabsTrigger>
                  <TabsTrigger value="media">Image & Links</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input 
                        value={formData.title} 
                        onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                        placeholder="Resume Revision"
                        data-testid="coaching-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Slug</Label>
                      <Input 
                        value={formData.slug} 
                        onChange={(e) => setFormData(p => ({ ...p, slug: e.target.value }))}
                        placeholder="resume-revision (auto-generated)"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Tagline</Label>
                    <Input 
                      value={formData.tagline} 
                      onChange={(e) => setFormData(p => ({ ...p, tagline: e.target.value }))}
                      placeholder="The resume that opens doors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Short Description *</Label>
                    <Textarea 
                      value={formData.short_description} 
                      onChange={(e) => setFormData(p => ({ ...p, short_description: e.target.value }))}
                      placeholder="Brief description shown on service cards"
                      rows={2}
                      data-testid="coaching-short-desc-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Full Description</Label>
                    <Textarea 
                      value={formData.hero_description} 
                      onChange={(e) => setFormData(p => ({ ...p, hero_description: e.target.value }))}
                      placeholder="Detailed description shown on the service detail page"
                      rows={4}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Icon</Label>
                      <select 
                        className="w-full border rounded-md p-2"
                        value={formData.icon}
                        onChange={(e) => setFormData(p => ({ ...p, icon: e.target.value }))}
                      >
                        {iconOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Display Order</Label>
                      <Input 
                        type="number"
                        value={formData.order} 
                        onChange={(e) => setFormData(p => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                  </div>

                  <div className="flex gap-6">
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={formData.is_popular} 
                        onCheckedChange={(c) => setFormData(p => ({ ...p, is_popular: c }))} 
                      />
                      <Label>Most Popular Badge</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={formData.is_active} 
                        onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))} 
                      />
                      <Label>Active</Label>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="pricing" className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>One-Time Price ($)</Label>
                      <Input 
                        type="number"
                        value={formData.pricing_one_time} 
                        onChange={(e) => setFormData(p => ({ ...p, pricing_one_time: parseInt(e.target.value) || 0 }))}
                        data-testid="pricing-onetime-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Installment Price ($)</Label>
                      <Input 
                        type="number"
                        value={formData.pricing_installment} 
                        onChange={(e) => setFormData(p => ({ ...p, pricing_installment: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Installment Frequency</Label>
                      <Input 
                        value={formData.pricing_installment_frequency} 
                        onChange={(e) => setFormData(p => ({ ...p, pricing_installment_frequency: e.target.value }))}
                        placeholder="bi-weekly"
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-4">Service Statistics (shown on cards)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Stat 1 Label</Label>
                        <Input 
                          value={formData.stat_1_label} 
                          onChange={(e) => setFormData(p => ({ ...p, stat_1_label: e.target.value }))}
                          placeholder="e.g., Clients Helped"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Stat 1 Value</Label>
                        <Input 
                          value={formData.stat_1_value} 
                          onChange={(e) => setFormData(p => ({ ...p, stat_1_value: e.target.value }))}
                          placeholder="e.g., 5,000+"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Stat 2 Label</Label>
                        <Input 
                          value={formData.stat_2_label} 
                          onChange={(e) => setFormData(p => ({ ...p, stat_2_label: e.target.value }))}
                          placeholder="e.g., Interview Rate"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Stat 2 Value</Label>
                        <Input 
                          value={formData.stat_2_value} 
                          onChange={(e) => setFormData(p => ({ ...p, stat_2_value: e.target.value }))}
                          placeholder="e.g., 85%"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="media" className="space-y-4">
                  <ImageUpload
                    label="Service Image"
                    value={formData.image}
                    onChange={(url) => setFormData(p => ({ ...p, image: url }))}
                  />

                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-4">Links</h4>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Learn More Link</Label>
                        <Input 
                          value={formData.learn_more_link} 
                          onChange={(e) => setFormData(p => ({ ...p, learn_more_link: e.target.value }))}
                          placeholder="/coaching/resume-revision"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CTA / Booking Link</Label>
                        <Input 
                          value={formData.cta_link} 
                          onChange={(e) => setFormData(p => ({ ...p, cta_link: e.target.value }))}
                          placeholder="https://careersupport.io/checkout/..."
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-coaching-service-btn">Save Service</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Services Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-0">
                <div className="h-40 bg-muted"></div>
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : services.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">No coaching services yet</p>
            <Button onClick={() => setDialogOpen(true)}>Add Your First Service</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <Card key={service.id} className="overflow-hidden group" data-testid={`coaching-service-${service.id}`}>
              <div className="relative h-40">
                {service.image ? (
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                    <DollarSign className="w-12 h-12 text-primary/50" />
                  </div>
                )}
                {service.is_popular && (
                  <Badge className="absolute top-2 left-2 bg-accent">
                    <Star className="w-3 h-3 mr-1" /> Most Popular
                  </Badge>
                )}
                {!service.is_active && (
                  <Badge variant="secondary" className="absolute top-2 right-2">
                    Inactive
                  </Badge>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-heading font-bold text-lg mb-1">{service.title}</h3>
                {service.tagline && (
                  <p className="text-sm text-muted-foreground mb-2">{service.tagline}</p>
                )}
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {service.short_description}
                </p>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {service.stat_1_value && (
                      <span className="flex items-center gap-1">
                        <BarChart className="w-3 h-3" /> {service.stat_1_value}
                      </span>
                    )}
                  </div>
                  <div className="text-lg font-bold text-primary">
                    ${service.pricing_one_time}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEdit(service)}>
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Service</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{service.title}"?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(service.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCoachingServices;
